import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { IPlayerState } from 'types/common';
import { bind } from 'utils/decorators/bind';

export class VideoEventsHandler {
  private _props: IVideoControllerComponentsCommonProps;
  private _config: IVideoElementControllerProps = {};
  private _logger: TLogger;
  private _timeUpdateEventWasLogged = false;

  private _eventsMap = {
    [EVideoElementControllerBusEvent.VIDEO_ELEMENT_RECREATED]: this._handleVideoElementRecreated,
    [EVideoElementControllerBusEvent.DESTROY]: this._handleDestroy,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._props = props;
    this._state = props.state;
    this._eventBus = props.eventBus;

    this._logger = Logger(this._eventBus, 'VideoElementController - VideoEventsHandler');
    this.init();
  }

  public setConfig(config: IVideoElementControllerProps) {
    this._config = config;
  }

  @bind
  public init() {
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  public toggleEvents(action: 'attach' | 'detach', overrideVideoElement?: HTMLVideoElement) {
    const target = overrideVideoElement || this._props.getVideoElement();

    if (!target) {
      return;
    }

    const method = action === 'attach' ? 'addEventListener' : 'removeEventListener';

    target[method]('click', this._onVideoClick);
    target[method]('playing', this._onPlayingHandler);
    target[method]('abort', this._onAbortHandler);
    target[method]('pause', this._onPauseHandler);
    target[method]('canplay', this._onCanPlayHandler);
    target[method]('error', this._onErrorHandler);
    target[method]('timeupdate', this._onTimeUpdateHandler);
    target[method]('loadstart', this._onLoadStartHandler);
    target[method]('loadeddata', this._onLoadedDataHandler);
    target[method]('waiting', this._onWaiting);
    target[method]('stalled', this._onStalled);
    target[method]('canplaythrough', this._onCanPlayThrough);
    target[method]('seeked', this._onSeeked);
    target[method]('ratechange', this._onRateChange);
    // // discuss
    target[method]('webkitendfullscreen', this._onEndFullscreenHandler);

    if (method === 'addEventListener') {
      this._logger.verbose(`event listeners are ATTACHED to the video element`);
    } else {
      this._logger.verbose(`event listeners are DETACHED from the video element`);
    }
  }

  @bind
  public _handleVideoElementRecreated() {
    this.toggleEvents('attach');
  }

  @bind
  private _handleDestroy() {
    this.toggleEvents('detach');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
  }

  private _onVideoClick = (e: Event) => {
    this._logger.verbose('"click" video native event fired');
    this._handleNativeEvent(e);
    this._eventBus.publish(EVideoElementControllerBusEvent.CLICK, {
      originalEvent: e,
    });
  };

  private _onSeeked = (e: Event) => {
    this._logger.verbose('"seeked" video native event fired');
    this._handleNativeEvent(e);
    this._eventBus.publish(EVideoElementControllerBusEvent.FORCE_PLAY_AFTER_PAUSE, {
      reason: 'seeked video native event',
    });
  };

  private _onRateChange = (e: Event) => {
    this._logger.verbose('"ratechange" video native event fired');
    this._handleNativeEvent(e);
    if (e.target && !(e.target as HTMLVideoElement).playbackRate) {
      this._eventBus.publish(EVideoElementControllerBusEvent.FORCE_PLAY_AFTER_PAUSE, {
        reason: 'video playbackRate is 0',
      });
    }
  };

  private _onCanPlayThrough = (e: Event) => {
    this._logger.verbose('"canplaythrough" video native event fired');
    const state = this._state.getState();
    this._handleNativeEvent(e);

    if (state.isStalled || state.isWaiting) {
      this._eventBus.publish(EVideoElementControllerBusEvent.FORCE_PLAY_AFTER_PAUSE, {
        reason:
          'canplaythrough video native event fired, and video is in the state of either stalled or waiting',
      });
    }
  };

  private _onPlayingHandler = (e: Event) => {
    this._logger.verbose('"playing" video native event fired');
    this._logger.info('Video has started playing');
    this._handleNativeEvent(e);

    this._eventBus.publish(EVideoElementControllerBusEvent.PLAYING, {
      isAutoplay: !this._state.getState().isAutoplayError,
    });
  };

  private _onAbortHandler = (e: Event) => {
    this._logger.verbose('"abort" video native event fired');
    this._handleNativeEvent(e);

    if (!e.defaultPrevented) {
      this._eventBus.publish(EVideoElementControllerBusEvent.LOADING, {});
    }
  };

  private _onPauseHandler = (e: Event) => {
    this._logger.verbose('"pause" video native event fired');
    this._handleNativeEvent(e);
    this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_PAUSE, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.FORCE_PLAY_AFTER_PAUSE, {
      reason: 'pause video event fired (most likely user paused the video manually)',
    });
  };

  private _onCanPlayHandler = (e: Event) => {
    this._logger.verbose('"canplay" video native event fired');
    this._handleNativeEvent(e);
    if (!e.defaultPrevented) {
      this._state.setState({ canPlay: true });
      this._eventBus.publish(EVideoElementControllerBusEvent.CAN_PLAY, {});
    }
  };

  private _onErrorHandler = (e: Event) => {
    this._logger.error('"error" video native event fired', { error: e });
    this._handleNativeEvent(e);
    this._eventBus.publish(EVideoElementControllerBusEvent.ERROR, {
      originalEvent: e,
    });

    if (!e.defaultPrevented) {
      const { MEDIA_ERR_SRC_NOT_SUPPORTED } = MediaError;
      const error = this._props.getVideoElement()?.error;
      const code = error?.code;

      if (code !== MEDIA_ERR_SRC_NOT_SUPPORTED) {
        this._logger.verbose(
          'Video element error is not of MEDIA_ERR_SRC_NOT_SUPPORTED kind. Requesting reset',
        );
        this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_RESET, {});
      }
    }
  };

  private _onTimeUpdateHandler = (e: Event) => {
    this._handleNativeEvent(e);
    if (!this._timeUpdateEventWasLogged) {
      this._logger.verbose('"timeupdate" video native event fired for the first time');
      this._timeUpdateEventWasLogged = true;
    }
    this._eventBus.publishSync(EVideoElementControllerBusEvent.TIME_UPDATE_NATIVE, {
      originalEvent: e,
    });
  };

  private _onLoadStartHandler = (e: Event) => {
    this._handleNativeEvent(e);
    this._logger.verbose('"loadstart" video native event fired');

    const { timeoutUpdateStream } = this._config;
    this._eventBus.publish(EVideoElementControllerBusEvent.LOADING, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.STREAM_RESET, {
      reason: 'onLoadStartHandler',
      timeout: timeoutUpdateStream,
    });
  };

  private _onLoadedDataHandler = (e: Event) => {
    this._logger.verbose('"loadeddata" video native event fired');
    this._handleNativeEvent(e);
    this._eventBus.publish(EVideoElementControllerBusEvent.LOADED, {});
  };

  private _onWaiting = (e: Event) => {
    this._logger.verbose('"waiting" video native event fired');
    const { timeoutWaitingStream } = this._config;
    this._handleNativeEvent(e);

    this._eventBus.publish(EVideoElementControllerBusEvent.WAITING, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.LOADING, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.STREAM_RESET, {
      reason: 'onWaiting',
      timeout: timeoutWaitingStream,
    });
  };

  private _onStalled = (e: Event) => {
    this._logger.verbose('"stalled" video native event fired');
    const { timeoutWaitingStream } = this._config;
    this._handleNativeEvent(e);

    this._eventBus.publish(EVideoElementControllerBusEvent.STALLED, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.STREAM_RESET, {
      reason: 'onStalled',
      timeout: timeoutWaitingStream,
    });
  };

  private _handleNativeEvent(e: Event) {
    this._eventBus.publishSync(EVideoElementControllerBusEvent.NATIVE_EVENT, {
      eventName: e.type as keyof HTMLVideoElementEventMap,
      event: e,
    });
  }

  private _onEndFullscreenHandler = (e: Event) => {
    this._handleNativeEvent(e);
    // this.config.onEndFullscreen!(e);
  };
}
